import React, { useState, useEffect } from "react";
import { Modal, Button, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import * as Icon from "react-bootstrap-icons";
import Swal from "sweetalert2";
import { reservationShortlistService } from "../service/reservationShortlist.service";
import { ReservationShortlist } from "../models/reservationShortlist.model";
import { useMilestone } from "../contexts/MilestoneContext";
import { userService } from "../service/user.service";

// Helper function for date parsing without timezone issues
const parseDateFromApi = (dateValue: string | Date | null | undefined): Date | null => {
  if (!dateValue) return null;
  if (dateValue instanceof Date) return dateValue;
  const dateStr = String(dateValue).split('T')[0];
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

interface ShortlistStaticProps {
  show: boolean;
  onHide: () => void;
  onStepChange?: (step: number) => void; // Add callback to communicate step changes
}

interface ShortlistItemWithDetails {
  id?: number;
  roomId: number;
  checkInDate: Date;
  checkOutDate: Date;
  status?: string;
  priority?: number;
  room?: {
    id: number;
    name: string;
    area?: number;
    numberOfBedrooms?: number;
    numberOfBathrooms?: number;
    isShowPrice?: boolean;
    currencySymbol?: string;
    roomType?: {
      name: string;
    };
    roomImages?: {
      imageUrl: string;
      fileName?: string;
    }[];
    roomCharges?: {
      amount: number;
      taxPercentage?: number | null;
    }[];
    rmcRoomCharges?: {
      amount: number;
      taxPercentage?: number | null;
    }[];
    property?: {
      id: number;
      name: string;
      address?: string;
    };
  };
  price?: number;
}

const ShortlistStatic: React.FC<ShortlistStaticProps> = ({ show, onHide, onStepChange }) => {
  const navigate = useNavigate();
  const userRole = useSelector((state: any) => state.auth.userRole) ?? "";
  const { setCurrentStep } = useMilestone();
  const [shortlistItems, setShortlistItems] = useState<
    ShortlistItemWithDetails[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingPriority, setUpdatingPriority] = useState<{
    [key: number]: boolean;
  }>({});
  const [submittingRequest, setSubmittingRequest] = useState<boolean>(false);
  const [removingItem, setRemovingItem] = useState<{ [key: number]: boolean }>(
    {}
  );
  // Control visibility of booking & summary section
  const [showBookingSection, setShowBookingSection] = useState<boolean>(false);
  // Track if user has clicked "Proceed Next" (for milestone progression)
  const [hasClickedProceedNext, setHasClickedProceedNext] = useState<boolean>(false);
  
  // Track current step for milestone
  const getCurrentStep = () => {
    if (!show) return 1; // When modal is closed, we're back to step 1 (View Details)
    if (!hasClickedProceedNext) return 2; // Shortlist opened, showing "Proceed Next" 
    return 3; // User clicked "Proceed Next", showing "Request Reservation"
  };

  // Notify parent component about step changes
  useEffect(() => {
    const currentStep = getCurrentStep();
    onStepChange?.(currentStep);
    setCurrentStep(currentStep);
  }, [show, hasClickedProceedNext, onStepChange, setCurrentStep]);
  // Booking-level guest counts (applies to all rooms)
  const [bookingGuestCounts, setBookingGuestCounts] = useState<{
    noOfAdults: number;
    noOfChildrens: number;
  }>({
    noOfAdults: 1,
    noOfChildrens: 0,
  });

  // Fetch user details first (for B2B users) to get guest counts and dates
  const fetchUserDetailsAndShortlist = async () => {
    try {
      const userDetails = await userService.getCurrentUserDetails();
      // B2B users have guest counts and dates in their address
      if (userDetails.address && userDetails.address.length > 0) {
        const userAddress = userDetails.address[0];
        const adultsFromProfile = userAddress.noOfAdults !== undefined && userAddress.noOfAdults !== null && userAddress.noOfAdults > 0 ? userAddress.noOfAdults : 1;
        const childrenFromProfile = userAddress.noOfChildrens !== undefined && userAddress.noOfChildrens !== null ? Math.max(0, userAddress.noOfChildrens) : 0;
        
        setBookingGuestCounts({
          noOfAdults: adultsFromProfile,
          noOfChildrens: childrenFromProfile,
        });
        
        console.log("Guest counts loaded from user profile:", {
          noOfAdults: adultsFromProfile,
          noOfChildrens: childrenFromProfile,
        });

        // Fetch shortlist items and then update their dates with user profile dates
        const items = await reservationShortlistService.getMyShortlistedItems();
        
        // If user has check-in/check-out dates in profile, use those to override shortlist item dates
        if (userAddress.checkIn && userAddress.checkOut) {
          const profileCheckIn = parseDateFromApi(userAddress.checkIn);
          const profileCheckOut = parseDateFromApi(userAddress.checkOut);
          
          console.log("Overriding shortlist dates with user profile dates:", {
            checkIn: profileCheckIn,
            checkOut: profileCheckOut,
          });
          
          // Update all shortlist items with profile dates (only if dates are valid)
          if (profileCheckIn && profileCheckOut) {
            const itemsWithUpdatedDates = items.map(item => ({
              ...item,
              checkInDate: profileCheckIn,
              checkOutDate: profileCheckOut,
            }));
            
            // Process items normally
            processShortlistItems(itemsWithUpdatedDates);
          } else {
            // Invalid dates, use original shortlist dates
            processShortlistItems(items);
          }
        } else {
          // No profile dates, use original shortlist dates
          processShortlistItems(items);
        }
        return; // Exit early since we're handling fetchShortlistedItems here
      }
    } catch (err) {
      console.error("Error fetching user details:", err);
    }
    
    // Fallback: fetch shortlist items normally if no user details
    fetchShortlistedItems();
  };

  // Fetch shortlisted items when modal opens
  useEffect(() => {
    if (show) {
      // reset the booking section visibility each time modal opens
      setShowBookingSection(false);
      setHasClickedProceedNext(false);

      // Fetch user details first (for B2B users) to get guest counts
      if (userRole === "B2BUser") {
        fetchUserDetailsAndShortlist();
      } else {
        fetchShortlistedItems();
      }
    }
  }, [show, userRole]);

  // Process shortlist items (common logic for both B2B and regular fetch)
  const processShortlistItems = (items: ReservationShortlist[]) => {
    setLoading(true);
    setError(null);
    try {
      // Transform the data to include room and property details
      console.log("Processing shortlisted items:", items);
      const itemsWithDetails = items.map((item, index) => {
        // Debug logging to understand the data structure
        console.log(`Item ${index} room data:`, item.room);
        console.log(`Item ${index} room charges:`, item.room?.roomCharges);

        // Extract room price based on user role
        let roomPrice = 25000; // fallback price
        
        if (userRole === "B2BUser") {
          // B2BUser should use RMC room charges
          if (item.room?.rmcRoomCharges && item.room.rmcRoomCharges.length > 0) {
            roomPrice = item.room.rmcRoomCharges[0].amount;
            console.log(`B2BUser - Using RMC room charge: ${roomPrice}`);
          } else {
            console.log(`B2BUser - No RMC room charges found, using fallback: ${roomPrice}`);
          }
        } else if (userRole === "B2CUser") {
          // B2CUser should use regular room charges
          if (item.room?.roomCharges && item.room.roomCharges.length > 0) {
            roomPrice = item.room.roomCharges[0].amount;
            console.log(`B2CUser - Using regular room charge: ${roomPrice}`);
          } else {
            console.log(`B2CUser - No regular room charges found, using fallback: ${roomPrice}`);
          }
        } else {
          // For other roles, try both sources with RMC taking priority
          if (item.room?.rmcRoomCharges && item.room.rmcRoomCharges.length > 0) {
            roomPrice = item.room.rmcRoomCharges[0].amount;
            console.log(`Other role - Using RMC room charge: ${roomPrice}`);
          } else if (item.room?.roomCharges && item.room.roomCharges.length > 0) {
            roomPrice = item.room.roomCharges[0].amount;
            console.log(`Other role - Using regular room charge: ${roomPrice}`);
          } else if (item.room?.amount && item.room.amount > 0) {
            roomPrice = item.room.amount;
            console.log(`Other role - Using direct room amount: ${roomPrice}`);
          }
        }

        console.log(`Item ${index} calculated price:`, roomPrice);

        const transformedItem = {
          ...item,
          priority: item.priority || index + 1, // Set default priority based on order if not set
          room: {
            id: item.roomId,
            name: item.room?.name || `Room ${item.roomId}`,
            area: item.room?.area || 1200,
            numberOfBedrooms: item.room?.numberOfBedrooms || 2,
            numberOfBathrooms: item.room?.numberOfBathrooms || 2,
            isShowPrice: item.room?.isShowPrice ?? false,
            currencySymbol: item.room?.currencySymbol || '₹',
            roomType: {
              name: item.room?.roomType?.name || "Unknown Room Type",
            },
            roomImages: item.room?.roomImages || [],
            amount: item.room?.amount || 0,
            roomCharges: item.room?.roomCharges || [],
            rmcRoomCharges: item.room?.rmcRoomCharges || [],
            property: {
              id: item.room?.property?.id || 1,
              name: item.room?.property?.name || "Unknown Property",
              address: item.room?.property?.address || "Unknown Address",
            },
          },
          price: roomPrice,
        };
        console.log(`Transformed item ${index}:`, transformedItem);
        return transformedItem;
      });
      setShortlistItems(itemsWithDetails);

      // Ensure priorities are compact/continuous after initial load
      setShortlistItems((prev) => {
        // Build unique sorted priority keys
        const uniqueKeys = Array.from(new Set(prev.map((i) => i.priority || 0))).filter((k) => k > 0) as number[];
        uniqueKeys.sort((a, b) => a - b);
        const mapping: { [old: number]: number } = {};
        uniqueKeys.forEach((oldPriority, idx) => (mapping[oldPriority] = idx + 1));
        const remapped = prev.map((it) => {
          const old = it.priority || 0;
          if (!mapping[old]) return { ...it, priority: uniqueKeys.length + 1 };
          return { ...it, priority: mapping[old] };
        });
        remapped.sort((a, b) => (a.priority || 0) - (b.priority || 0));
        return remapped;
      });

      // Booking-level guest counts are already initialized in state
      // No need to set per-room guest counts anymore
    } catch (err) {
      setError("Failed to load shortlisted items");
      console.error("Error processing shortlisted items:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch shortlisted items (wrapper function)
  const fetchShortlistedItems = async () => {
    try {
      const items = await reservationShortlistService.getMyShortlistedItems();
      processShortlistItems(items);
    } catch (err) {
      setError("Failed to load shortlisted items");
      console.error("Error fetching shortlisted items:", err);
      setLoading(false);
    }
  };

  // Remove item by roomId (roomId is the stable identifier we use for UI state)
  const handleRemoveItem = async (roomId: number) => {
    // Find the item to get room details for confirmation
    const itemToRemove = shortlistItems.find((item) => item.roomId === roomId);
    if (!itemToRemove) {
      console.error("Item not found in shortlist");
      return;
    }

    // Show confirmation dialog
    const result = await Swal.fire({
      title: "Remove from Shortlist?",
      text: `Are you sure you want to remove "${itemToRemove.room?.property?.name}" from your shortlist?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, remove it!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    // Set loading state for this item
    setRemovingItem((prev) => ({ ...prev, [roomId]: true }));

    try {
      // Use roomId for the API call
      await reservationShortlistService.removeFromShortlist(roomId);

      // Remove item from local state and compact priorities so there are no gaps
      setShortlistItems((prev) => {
        const filteredItems = prev.filter((item) => item.roomId !== roomId);
        // Compact priorities
        const uniqueKeys = Array.from(new Set(filteredItems.map((i) => i.priority || 0))).filter((k) => k > 0) as number[];
        uniqueKeys.sort((a, b) => a - b);
        const mapping: { [old: number]: number } = {};
        uniqueKeys.forEach((oldPriority, idx) => (mapping[oldPriority] = idx + 1));
        const remapped = filteredItems.map((it) => {
          const old = it.priority || 0;
          if (!mapping[old]) return { ...it, priority: uniqueKeys.length + 1 };
          return { ...it, priority: mapping[old] };
        });
        remapped.sort((a, b) => (a.priority || 0) - (b.priority || 0));
        return remapped;
      });

      // Show success message
      Swal.fire({
        icon: "success",
        title: "Removed!",
        text: "Property has been removed from your shortlist.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } catch (err) {
      console.error("Error removing item from shortlist:", err);

      // Show error message
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to remove property from shortlist. Please try again.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      // Clear loading state
      setRemovingItem((prev) => ({ ...prev, [roomId]: false }));
    }
  };

  // Implement priority-shift logic per user's specification:
  // - When an item is assigned to newPriority, set that item's priority = newPriority.
  // - For other items where priority > newPriority, decrement their priority by 1.
  // This produces the behavior in your example (multiple items can share the same priority value)
  const handlePriorityChange = async (roomId: number, newPriority: number) => {
    console.log("handlePriorityChange called:", { roomId, newPriority });

    if (!roomId) {
      console.error("roomId is required for priority change");
      return;
    }

    // Find the current item
    const currentItem = shortlistItems.find((item) => item.roomId === roomId);

    if (!currentItem) {
      console.error("Item not found in shortlist");
      return;
    }

    // If the new priority is the same as current, do nothing
    if (currentItem.priority === newPriority) {
      return;
    }

    // Set loading state for this item only (keyed by roomId)
    setUpdatingPriority((prev) => ({
      ...prev,
      [roomId]: true,
    }));

    try {

      console.log("Calling updatePriority API...");
      console.log(`Updating priority: Room ${roomId} (${currentItem.priority} → ${newPriority})`);

      // Call API with roomId
      await reservationShortlistService.updatePriority(roomId, newPriority);

      // Update local state and then compact priorities so there are no gaps.
      // Example: after setting priorities like [1,1,2,4] -> compact to [1,1,2,3]
      setShortlistItems((prev) => {
        // 1) apply the requested change
        const applied = prev.map((item) =>
          item.roomId === roomId ? { ...item, priority: newPriority } : item
        );

        // 2) compute unique priority keys in ascending numeric order
        const uniqueKeys = Array.from(
          new Set(applied.map((i) => i.priority || 0))
        ).filter((k) => k > 0) as number[];
        uniqueKeys.sort((a, b) => a - b);

        // 3) build mapping from old priority -> compacted priority (1..n)
        const mapping: { [old: number]: number } = {};
        uniqueKeys.forEach((oldPriority, idx) => {
          mapping[oldPriority] = idx + 1;
        });

        // 4) remap every item's priority using mapping (if priority missing or 0, assign end)
        const remapped = applied.map((it) => {
          const old = it.priority || 0;
          // If somehow priority is 0 or not in mapping, place it at the end (mapping size + 1)
          if (!mapping[old]) {
            return { ...it, priority: uniqueKeys.length + 1 };
          }
          return { ...it, priority: mapping[old] };
        });

        // 5) keep UI ordered by priority ascending
        remapped.sort((a, b) => (a.priority || 0) - (b.priority || 0));
        return remapped;
      });

      Swal.fire({
        icon: "success",
        title: "Priority Updated",
        text: `Priority has been updated to ${newPriority}`,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2000,
      });

      console.log("Priority updated successfully:", roomId, newPriority);
    } catch (error: any) {
      console.error("Error updating priority:", error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update priority. Please try again.";

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: errorMessage,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 4000,
      });
    } finally {
      setUpdatingPriority((prev) => ({
        ...prev,
        [roomId]: false,
      }));
    }
  };

  const selectedCount = shortlistItems.length;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header
        closeButton
        style={{ border: "none", paddingBottom: "10px" }}
      >
        <Modal.Title
          style={{ fontSize: "24px", fontWeight: "bold", color: "#333" }}
        >
          My Shortlist 
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: "0 24px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <div>Loading your shortlisted items...</div>
          </div>
        ) : error ? (
          <div
            style={{ textAlign: "center", padding: "40px", color: "#dc3545" }}
          >
            <div>{error}</div>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={fetchShortlistedItems}
              style={{ marginTop: "10px" }}
            >
              Try Again
            </Button>
          </div>
        ) : shortlistItems.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Icon.Heart
              size={48}
              style={{ color: "#ccc", marginBottom: "15px" }}
            />
            <div style={{ color: "#666" }}>Your shortlist is empty</div>
            <div style={{ color: "#999", fontSize: "14px", marginTop: "5px" }}>
              Start adding rooms to your shortlist to see them here
            </div>
          </div>
        ) : (
          <>
            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
              {shortlistItems.map((item, index) => (
                <div key={`r${item.roomId}`}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "16px 0",
                      borderBottom:
                        index === shortlistItems.length - 1
                          ? "none"
                          : "1px solid #eee",
                    }}
                  >
                    {/* Property Image */}
                    <div style={{ marginRight: "16px" }}>
                      <img
                        src={
                          item.room?.roomImages &&
                          item.room.roomImages.length > 0
                            ? item.room.roomImages[0].imageUrl || "/Images/room-placeholder.jpg"
                            : "/Images/room-placeholder.jpg"
                        }
                        alt={item.room?.name || "Room"}
                        style={{
                          width: "80px",
                          height: "80px",
                          borderRadius: "8px",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          // Fallback to placeholder if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.src = "/Images/room-placeholder.jpg";
                        }}
                      />
                    </div>

                    {/* Property Details */}
                    <div style={{ flex: 1 }}>
                      <h6
                        style={{
                          fontWeight: "bold",
                          margin: "0 0 8px 0",
                          fontSize: "16px",
                          color: "#333",
                        }}
                      >
                        {item.room?.property?.name || "Property Name"}
                      </h6>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#666",
                          marginBottom: "4px",
                        }}
                      >
                        {item.room?.name} - {item.room?.roomType?.name}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "20px",
                          color: "#666",
                          fontSize: "14px",
                        }}
                      >
                        <span>{item.room?.numberOfBedrooms || 2} Beds</span>
                        <span>{item.room?.numberOfBathrooms || 2} Baths</span>
                        <span>{item.room?.area || 1200} sqft</span>
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#999",
                          marginTop: "4px",
                        }}
                      >
                        Check-in:{" "}
                        {new Date(item.checkInDate).toLocaleDateString()} |
                        Check-out:{" "}
                        {new Date(item.checkOutDate).toLocaleDateString()}
                      </div>

                      {/* Room details - no per-room guest counts anymore */}
                    </div>

                    {/* Priority Dropdown */}
                    <div style={{ marginRight: "16px" }}>
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#666",
                            marginBottom: "4px",
                          }}
                        >
                          Priority{" "}
                          {updatingPriority[item.roomId] && (
                            <span
                              style={{ fontSize: "10px", color: "#007bff" }}
                            >
                              ...
                            </span>
                          )}
                        </div>
                        <select
                          value={item.priority || index + 1}
                          disabled={!!updatingPriority[item.roomId]}
                          style={{
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            padding: "4px 8px",
                            fontSize: "14px",
                            width: "72px",
                            textAlign: 'center',
                            WebkitAppearance: 'menulist-button',
                            MozAppearance: 'menulist-button',
                            opacity: updatingPriority[item.roomId] ? 0.6 : 1,
                          }}
                          onChange={(e) => {
                            const newPriority = parseInt(e.target.value);
                            console.log("Priority change triggered:", {
                              roomId: item.roomId,
                              currentPriority: item.priority,
                              newPriority: newPriority,
                            });
                            handlePriorityChange(item.roomId, newPriority);
                          }}
                        >
                          {
                            // Determine allowed/disabled options:
                            // - Compute the highest currently assigned priority across items
                            // - The "next allowed" priority is maxAssigned + 1 but it should not
                            //   exceed the number of shortlist items (so we don't allow gaps)
                            (() => {
                              // Use compacted unique priorities to determine the next allowed value.
                              const unique = Array.from(new Set(shortlistItems.map((i) => i.priority || 0))).filter((k) => k > 0) as number[];
                              unique.sort((a, b) => a - b);
                              const nextAllowed = Math.min(unique.length + 1, shortlistItems.length);

                              // Options run from 1..shortlistItems.length. Disable values > nextAllowed
                              // except keep the item's current priority selectable so we don't force-remove it.
                              const maxOption = shortlistItems.length || nextAllowed;

                              return Array.from({ length: maxOption }, (_, i) => {
                                const priorityValue = i + 1;
                                const disabled = priorityValue > nextAllowed && priorityValue !== (item.priority || 0);
                                return (
                                  <option key={priorityValue} value={priorityValue} disabled={disabled}>
                                    {priorityValue}
                                  </option>
                                );
                              });
                            })()
                          }
                        </select>
                      </div>
                    </div>

                    {/* Delete Button */}
                    <div style={{ marginRight: "16px" }}>
                      <button
                        style={{
                          background: "none",
                          border: "1px solid #dc3545",
                          borderRadius: "4px",
                          padding: "6px 8px",
                          color: removingItem[item.roomId]
                            ? "#999"
                            : "#dc3545",
                          cursor: removingItem[item.roomId]
                            ? "not-allowed"
                            : "pointer",
                          fontSize: "12px",
                          opacity: removingItem[item.roomId] ? 0.6 : 1,
                        }}
                        title="Remove from shortlist"
                        disabled={!!removingItem[item.roomId]}
                        onClick={() => handleRemoveItem(item.roomId)}
                      >
                        {removingItem[item.roomId] ? "⏳" : "🗑"}
                      </button>
                    </div>

                    {/* Price */}
                    {item.room?.isShowPrice && (
                      <div
                        style={{
                          textAlign: "right",
                          color: "#007bff",
                          fontWeight: "bold",
                          fontSize: "18px",
                        }}
                      >
                        {item.room?.currencySymbol || '₹'}{(item.price || 25000).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Booking-level Guest Count Section */}
            {showBookingSection ? (
            <div
              style={{
                borderTop: "1px solid #eee",
                paddingTop: "16px",
                marginTop: "16px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  marginBottom: "12px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "#333",
                }}
              >
                Guest Information (applies to all rooms)
              </div>
              <div
                style={{ display: "flex", gap: "20px", alignItems: "center" }}
              >
                <div>
                  <label
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      display: "block",
                      marginBottom: "4px",
                    }}
                  >
                    Adults *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={bookingGuestCounts.noOfAdults}
                    onChange={(e) => {
                      const value = Math.max(1, parseInt(e.target.value) || 1);
                      setBookingGuestCounts((prev) => ({
                        ...prev,
                        noOfAdults: value,
                      }));
                    }}
                    style={{
                      width: "60px",
                      padding: "6px 8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      display: "block",
                      marginBottom: "4px",
                    }}
                  >
                    Children
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={bookingGuestCounts.noOfChildrens}
                    onChange={(e) => {
                      const value = Math.max(0, parseInt(e.target.value) || 0);
                      setBookingGuestCounts((prev) => ({
                        ...prev,
                        noOfChildrens: value,
                      }));
                    }}
                    style={{
                      width: "60px",
                      padding: "6px 8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  />
                </div>
              </div>
              {/* Editable check-in and check-out date fields */}
              <div style={{ display: "flex", gap: "20px", alignItems: "center", marginTop: "12px" }}>
                <div>
                  <label style={{ fontSize: "12px", color: "#666", display: "block", marginBottom: "4px" }}>Check-In Date *</label>
                  <input
                    type="date"
                    value={shortlistItems.length > 0 ? (() => {
                      const d = new Date(shortlistItems[0].checkInDate);
                      const year = d.getFullYear();
                      const month = String(d.getMonth() + 1).padStart(2, '0');
                      const day = String(d.getDate()).padStart(2, '0');
                      return `${year}-${month}-${day}`;
                    })() : ''}
                    onChange={e => {
                      // Parse date from YYYY-MM-DD format without timezone conversion
                      const [year, month, day] = e.target.value.split('-').map(Number);
                      const newDate = new Date(year, month - 1, day, 12, 0, 0, 0);
                      // Update ALL shortlist items with the new check-in date
                      setShortlistItems(items => items.map(item => ({ ...item, checkInDate: newDate })));
                      console.log("Updated all shortlist items with new check-in date:", newDate);
                    }}
                    style={{ width: "140px", padding: "6px 8px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px" }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "12px", color: "#666", display: "block", marginBottom: "4px" }}>Check-Out Date *</label>
                  <input
                    type="date"
                    value={shortlistItems.length > 0 ? (() => {
                      const d = new Date(shortlistItems[0].checkOutDate);
                      const year = d.getFullYear();
                      const month = String(d.getMonth() + 1).padStart(2, '0');
                      const day = String(d.getDate()).padStart(2, '0');
                      return `${year}-${month}-${day}`;
                    })() : ''}
                    onChange={e => {
                      // Parse date from YYYY-MM-DD format without timezone conversion
                      const [year, month, day] = e.target.value.split('-').map(Number);
                      const newDate = new Date(year, month - 1, day, 12, 0, 0, 0);
                      // Update ALL shortlist items with the new check-out date
                      setShortlistItems(items => items.map(item => ({ ...item, checkOutDate: newDate })));
                      console.log("Updated all shortlist items with new check-out date:", newDate);
                    }}
                    style={{ width: "140px", padding: "6px 8px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px" }}
                  />
                </div>
              </div>
            </div>
            ) : (
              // Proceed Next CTA when booking section is hidden
                <div style={{display: 'flex', justifyContent: 'space-between', padding: '24px 0' }}>
                <Button
                  variant="primary"
                  onClick={() => {
                    window.location.href = '/b2b-property';
                  }}
                  style={{ backgroundColor: '#2196F3', borderColor: '#2196F3', color: '#fff', padding: '12px 32px', fontWeight: '600' }}
                >
                  Search More
                </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setShowBookingSection(true);
                  setHasClickedProceedNext(true);
                }}
                style={{ backgroundColor: '#ff5722', borderColor: '#ff5722', color: '#fff', padding: '12px 32px', fontWeight: '600' }}
              >
                Proceed Next
              </Button>
            </div>
            )}

            {/* Summary Section */}
            {showBookingSection && (
              <div
                style={{
                  borderTop: "1px solid #eee",
                  paddingTop: "16px",
                  marginTop: "16px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <div
                    style={{
                      color: "#666",
                      fontSize: "16px",
                    }}
                  >
                    Selected Properties
                  </div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "bold",
                    }}
                  >
                    {selectedCount}
                  </div>
                </div>
                <div
                  style={{
                    backgroundColor: "#f8f9fa",
                    padding: "12px",
                    borderRadius: "6px",
                    marginBottom: "20px",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#495057",
                      lineHeight: "1.4",
                    }}
                  >
                    <strong>How it works:</strong> Our team will review your
                    preferences and book <strong>one available room</strong> based
                    on your priority order and room availability on your selected
                    dates.
                    <div style={{ marginTop: '8px', fontSize: '13px', color: '#6c757d' }}>
                      Note: You can assign the same priority number to multiple rooms in the UI. On submission
                      we will convert these into a unique sequential ordering (preserving your selected groups)
                      so the request is accepted by the system.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      {showBookingSection && shortlistItems.length > 0 && !loading && (
        <Modal.Footer style={{ border: "none", padding: "0 24px 24px 24px" }}>
          <Button
            disabled={submittingRequest}
            style={{
              backgroundColor: "#ff5722",
              borderColor: "#ff5722",
              color: "white",
              fontWeight: "bold",
              fontSize: "16px",
              padding: "12px 0",
              width: "100%",
              border: "none",
              borderRadius: "6px",
              opacity: submittingRequest ? 0.7 : 1,
            }}
            onClick={async () => {
              try {
                setSubmittingRequest(true);
               
                const bookingCheckIn = shortlistItems.length > 0 ? new Date(shortlistItems[0].checkInDate) : null;
                const bookingCheckOut = shortlistItems.length > 0 ? new Date(shortlistItems[0].checkOutDate) : null;
                const itemsForReservation = shortlistItems.filter((item) => item.roomId && (item.priority !== undefined && item.priority !== null));

                // Capture original index to stable-sort duplicates
                const indexed = itemsForReservation.map((it, idx) => ({ it, originalIndex: idx }));

                indexed.sort((a, b) => {
                  const pa = a.it.priority || 0;
                  const pb = b.it.priority || 0;
                  if (pa !== pb) return pa - pb;
                  return a.originalIndex - b.originalIndex;
                });

                // Assign sequential unique priorities
                const mappedPriorities: { [roomId: number]: number } = {};
                indexed.forEach((entry, i) => {
                  mappedPriorities[entry.it.roomId] = i + 1;
                });

                const reservations = itemsForReservation.map((item) => ({
                  roomId: item.roomId,
                  priority: mappedPriorities[item.roomId] || (item.priority || 1),
                  noOfAdults: bookingGuestCounts.noOfAdults,
                  noOfChildrens: bookingGuestCounts.noOfChildrens,
                  checkIn: bookingCheckIn ? bookingCheckIn.toISOString() : new Date(item.checkInDate).toISOString(),
                  checkOut: bookingCheckOut ? bookingCheckOut.toISOString() : new Date(item.checkOutDate).toISOString(),
                }));

                if (reservations.length === 0) {
                  console.warn(
                    "No valid shortlist items to request reservation for"
                  );
                  Swal.fire({
                    toast: true,
                    position: "bottom",
                    icon: "warning",
                    title: "No items available for reservation request",
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                  });
                  return;
                }

                console.log(
                  "Requesting reservation for shortlisted properties:",
                  reservations
                );

                const response =
                  await reservationShortlistService.createReserveRequest(
                    reservations
                  );
                console.log("Reserve request created successfully:", response);

                // Navigate to thank you page instead of showing toast
                navigate('/reservation-thank-you');
                onHide();
              } catch (error: any) {
                console.error("Error creating reserve request:", error);

                // Handle specific error messages from backend
                let errorMessage =
                  "Failed to submit reservation request. Please try again.";

                if (error?.response?.data) {
                  // Check for already booked message
                  if (error.response.data.includes("already booked")) {
                    errorMessage =
                      "Some rooms are already booked and cannot be reserved. Please remove them from your shortlist and try again.";
                  }
                  // Use backend message if available
                  else if (typeof error.response.data === "string") {
                    errorMessage = error.response.data;
                  }
                  // Handle structured error response
                  else if (error.response.data.message) {
                    errorMessage = error.response.data.message;
                  }
                }

                Swal.fire({
                  toast: true,
                  position: "bottom",
                  icon: "warning",
                  title: errorMessage,
                  showConfirmButton: false,
                  timer: 5000,
                  timerProgressBar: true,
                });
              } finally {
                setSubmittingRequest(false);
              }
            }}
          >
            {submittingRequest ? "Submitting..." : "Request Reservation"}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default React.memo(ShortlistStatic);
